import { Outlet, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { OfflineBanner } from "./OfflineBanner";
import { ToastViewport } from "./Toast";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { useBranding } from "@/lib/branding";
import { applyTheme } from "@/lib/theme";

export function AppShell() {
  const navigate = useNavigate();
  // Narrow viewports hide the sidebar by default; the user must click the
  // burger / a "menu" affordance to reveal it. Medium viewports (820-1023)
  // were the broken band — sidebar was unmounted and the burger CSS only
  // showed at <=820px, so users had no way to bring it back. The new layout
  // keeps the sidebar mounted and tracks an explicit `collapsed` state so
  // both the user-toggle and the viewport-derived default share one source
  // of truth.
  const isNarrow = useMediaQuery("(max-width: 1023px)");
  const [userCollapsed, setUserCollapsed] = useState<boolean | null>(null);
  // Effective state: explicit user toggle wins, otherwise the viewport
  // decides (collapsed at <=1023px, open above).
  const collapsed = userCollapsed ?? isNarrow;
  const { appName } = useBranding();
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.me(),
    queryFn: api.me,
    retry: false,
  });

  useEffect(() => {
    if (isError) navigate({ to: "/login" });
  }, [isError, navigate]);

  useEffect(() => {
    document.title = appName;
  }, [appName]);

  // Ensure data-theme is applied on mount (and re-applied if it gets cleared).
  useEffect(() => {
    applyTheme();
  }, []);

  if (isLoading || !data) return null;

  return (
    <div className={`app${collapsed ? " sidebar-collapsed" : ""}`}>
      <Sidebar
        collapsed={collapsed}
        onCollapse={() => setUserCollapsed(true)}
        onNavigate={() => {
          // Auto-collapse after navigation on narrow viewports so the
          // sidebar doesn't keep covering the content.
          if (isNarrow) setUserCollapsed(true);
        }}
      />
      <main
        style={{
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <OfflineBanner />
        <TopBar
          showMenu={collapsed}
          onMenu={() => setUserCollapsed(collapsed ? false : true)}
        />
        <div style={{ flex: 1, overflow: "auto" }}>
          <AnnouncementBanner />
          <Outlet />
        </div>
      </main>
      <ToastViewport />
    </div>
  );
}
